import React, { memo, useEffect, useMemo, useRef } from "react";
import {
  ClearAll,
  ConfigFilterOption,
  Filter,
  FiltersSelected,
  useFilters,
  useResults,
  useSortBy,
} from "@usereactify/search";
import { Drawer } from "@app/components/Drawer";
import { AccordionItem } from "@app/components/Drawer/AccordionItem";
import { FilterChip } from "@app/components/SortDrawer/FilterChip";
import { useSortDrawer } from "@app/context/SortDrawerContext";
import { initAccordionItems } from "@app/components/accordion-item";

export const SortDrawer = memo(() => {
  const ref = useRef<HTMLDivElement>(null);

  const { sortOption, sortOptions, setSortOption } = useSortBy();
  const { filters, selected } = useFilters();
  const { resultStats } = useResults();

  const { isSortDrawerOpen, setIsSortDrawerOpen } = useSortDrawer();

  const totalResults = useMemo(() => {
    return resultStats?.displayedResults || 0;
  }, [resultStats]);

  const displaySortOptions = useMemo(
    () =>
      sortOptions?.sort((a, b) => (a.position === b.position ? (a.name > b.name ? 1 : -1) : a.position - b.position)) ||
      [],
    [sortOptions],
  );

  const displayFilters = useMemo(
    () =>
      filters?.sort((a, b) => (a.position === b.position ? (a.name > b.name ? 1 : -1) : a.position - b.position)) || [],
    [filters],
  );

  const sortItem = useMemo(
    () => ({
      id: "sort",
      title: "Sort by",
      isOpen: isSortDrawerOpen === "sort" || typeof isSortDrawerOpen !== "string",
      content: (
        <>
          {displaySortOptions.map((option) => {
            return (
              <label key={option.id} className="radio-item" htmlFor={option.id}>
                <input
                  className="radio-input"
                  type="radio"
                  id={option.id}
                  name={option.id}
                  value={option.id}
                  checked={sortOption?.id === option.id}
                  onChange={() => setSortOption(option.handle)}
                />
                <div>{option.name}</div>
              </label>
            );
          })}
        </>
      ),
    }),
    [sortOption, displaySortOptions, isSortDrawerOpen],
  );

  useEffect(() => {
    if (isSortDrawerOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }, [isSortDrawerOpen]);

  return (
    <Drawer position="left" isOpen={!!isSortDrawerOpen} onClose={() => setIsSortDrawerOpen(false)}>
      <div ref={ref} className="h-full flex flex-col">
        <div className="flex gap-4 items-center justify-between py-2 px-4 pr-1.5">
          <div className="text-lg text-brand-bistre">Filter & Sort</div>
          <button
            className="w-[44px] h-[44px] flex items-center justify-center"
            dangerouslySetInnerHTML={{ __html: window.iconCloseMd }}
            onClick={() => setIsSortDrawerOpen(false)}
          />
        </div>

        {selected.length > 0 && (
          <div className="px-4 pb-4 flex flex-wrap gap-2">
            <FiltersSelected
              render={({ selectedFilters, handleRemove }) => (
                <>
                  {selectedFilters.map((filter) => (
                    <>
                      {Array.isArray(filter.value) ? (
                        filter.value.map((v) => (
                          <FilterChip
                            key={filter.key + v}
                            label={typeof v === "string" ? v : v.label}
                            onClose={() => handleRemove(filter.key, typeof v === "string" ? [v] : undefined)}
                          />
                        ))
                      ) : (
                        <FilterChip
                          key={filter.key}
                          label={typeof filter.value === "string" ? filter.value : filter.value.label}
                          onClose={() => handleRemove(filter.key)}
                        />
                      )}
                    </>
                  ))}
                </>
              )}
            />

            <ClearAll
              render={({ clearAll }) => {
                return (
                  <button className="button button--small button--text button--black" onClick={clearAll}>
                    <span className="button-text">Clear All</span>
                  </button>
                );
              }}
            />
          </div>
        )}

        <div className="flex-1 overflow-auto border-t border-b-neutrals-lightGrey">
          <AccordionItem item={sortItem} />
          {displayFilters.map((filter) => (
            <FilterAccordionItem key={filter.id} filter={filter} />
          ))}
        </div>

        <div className="p-4 flex gap-2 bg-neutrals-lightGrey">
          <ClearAll
            render={({ clearAll }) => {
              return (
                <button
                  className="button button--medium button--solid button--white"
                  onClick={() => {
                    clearAll();
                    setIsSortDrawerOpen(false);
                  }}
                >
                  <span className="button-text">Clear</span>
                </button>
              );
            }}
          />

          <button
            className="flex-1 button button--medium button--solid button--black"
            onClick={() => setIsSortDrawerOpen(false)}
          >
            <span className="button-text">
              View {totalResults} Result{totalResults > 1 || totalResults === 0 ? "s" : ""}
            </span>
          </button>
        </div>
      </div>
    </Drawer>
  );
});

const FilterAccordionItem = ({ filter }: { filter: ConfigFilterOption }) => {
  const { isSortDrawerOpen, setIsSortDrawerOpen } = useSortDrawer();

  useEffect(() => {
    initAccordionItems();
  }, []);

  return (
    <>
      <AccordionItem
        key={filter.id}
        item={{
          id: filter.id,
          title: filter.name,
          isOpen: isSortDrawerOpen === filter.name.toLowerCase(),
          content: (
            <>
              <Filter
                key={filter.id}
                filter={filter}
                renderFilterList={({ filterListProps }) => {
                  const isSingle = filterListProps.filter.displayType === "single";
                  return (
                    <>
                      {filterListProps.options.map((option: any) => {
                        return (
                          <label className={isSingle ? "radio-item" : "checkbox-item"} htmlFor={option.key}>
                            <input
                              className={isSingle ? "radio-input" : "checkbox-input"}
                              type={isSingle ? "radio" : "checkbox"}
                              id={option.key}
                              name={option.key}
                              value={option.key}
                              checked={option.checked}
                              onChange={(e) => filterListProps.handleChange(option.key)}
                            />

                            <div className={isSingle ? "radio-label" : "checkbox-label"}>
                              <div className={`${isSingle ? "radio-label-main" : "checkbox-label-main"} capitalize`}>
                                {option.label.toLowerCase()}
                              </div>
                              {option.doc_count ? (
                                <div className={`${isSingle ? "radio-label-sub" : "checkbox-label-sub"}`}>
                                  ({option.doc_count})
                                </div>
                              ) : null}
                            </div>
                          </label>
                        );
                      })}
                    </>
                  );
                }}
                showEmptyFilters
              />
            </>
          ),
        }}
        twoLevelParent
        onOpen={() => {
          setIsSortDrawerOpen(filter.name.toLowerCase());
        }}
      />
    </>
  );
};
